export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiInit = Omit<RequestInit, "body"> & { body?: unknown };

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12_000);
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  let body: BodyInit | undefined;
  if (init.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.body);
  }
  try {
    const response = await fetch(path, { ...init, headers, body, signal: controller.signal });
    const payload = (await response.json().catch(() => ({}))) as { error?: string; message?: string; code?: string } & T;
    if (!response.ok) throw new ApiError(payload.message ?? payload.error ?? "Something went wrong.", response.status, payload.code);
    return payload;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("The request took too long. Please try again.", 408, "timeout");
    }
    throw new ApiError("You appear to be offline. Check your connection and try again.", 0, "offline");
  } finally {
    window.clearTimeout(timeout);
  }
}
